import React, { useCallback, useMemo, useState } from 'react';

import { EllipsisIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Divider } from '@/components/organisms/Divider/Divider';
import { DeskDropdown } from '@/components/organisms/PageSubHeader/DeskDropdown';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useDesks from '@/hooks/useDesks';
import { useNav } from '@/modules/humanChat/hooks/useNav';
import {
  selectViewSelectedAgent,
  selectViewSelectedDepartment,
  selectViewTitle,
  selectViewSelectedBrain,
} from '@/modules/humanChat/redux/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';

import NavigationItem from './NavigationItem';
import { PinMenuContent } from '../../../modules/humanChat/components/LeftSidebar/PinMenuContent';

const menuButtonClass =
  'h-10 w-16 px-3 py-2.5 hover:bg-white/40 data-[active=true]:bg-white/40 data-[active=true]:text-informational';

/**
 * Special menu button component for ellipsis menus
 */
const MenuButton = ({ icon, selected }: { icon: React.JSX.Element; selected: boolean }) => {
  return (
    <NavigationMenuLink asChild className={menuButtonClass}>
      <button type="button" data-active={selected}>
        {icon}
      </button>
    </NavigationMenuLink>
  );
};

const ChatNavigationIframe = () => {
  const {
    conversationsMenu,
    filteredTeammatesMenu,
    teammatesMenu,
    pinnedAgents,
    departmentsMenu,
    pinnedDepartments,
    filteredDepartmentsMenu,
  } = useNav();
  const selectedAgent = useSelector(selectViewSelectedAgent);
  const selectedDepartment = useSelector(selectViewSelectedDepartment);
  const title = useSelector(selectViewTitle);
  const selectedBrain = useSelector(selectViewSelectedBrain);
  const { serviceDesks } = useDesks();
  const slug = useSelector(selectAccountSlug);

  /**
   * Normalizes teammates menu items for the PinMenuPopover
   * Handles disabled pins logic for brain agents
   */
  const normalizeTeammatesForPinMenu = useMemo(() => {
    return teammatesMenu.map((mate) => {
      const isBrain = mate.id === 'brain';
      const isPinned = pinnedAgents.includes(mate.id) || isBrain;

      return {
        ...mate,
        isPinned,
        isDisabled: isBrain, // Brain agents cannot be unpinned
      };
    });
  }, [teammatesMenu, pinnedAgents]);

  /**
   * Normalizes departments menu items for the PinMenuPopover
   * Handles disabled pins logic for user departments
   */
  const normalizeDepartmentsForPinMenu = useMemo(() => {
    return departmentsMenu.map((dept) => {
      const isPinned = pinnedDepartments.includes(dept.id) || dept.pinned;

      return {
        ...dept,
        isPinned,
        isDisabled: dept.pinned, // Departments can always be pinned/unpinned
      };
    });
  }, [departmentsMenu, pinnedDepartments]);

  const selected = useCallback(
    (id: string) => {
      if (title) {
        const converted = title === 'me' ? 'inbox' : title;
        return converted === id;
      }

      if (selectedAgent) {
        return selectedAgent === id;
      }

      if (selectedBrain) {
        return selectedBrain === id;
      }

      if (selectedDepartment) {
        return selectedDepartment === id;
      }

      return false;
    },
    [selectedAgent, selectedBrain, selectedDepartment, title]
  );

  const { t } = useTranslation();
  const [agentsOpen, setAgentsOpen] = useState(false);
  const [departmentsOpen, setDepartmentsOpen] = useState(false);

  return (
    <nav className="flex h-(--newheader-height) items-center justify-between gap-1 bg-linear-to-r from-blue-950 from-25% to-informational px-8">
      <NavigationMenu className="justify-start">
        <NavigationMenuList className="flex w-full gap-1">
          {conversationsMenu.map((conversation) => {
            const { notificationKey, text, icon, onClick, sum, id } = conversation;
            return (
              <NavigationMenuItem key={id}>
                <NavigationItem
                  notificationKey={notificationKey}
                  tooltipContent={text}
                  icon={icon}
                  onClick={onClick}
                  sum={sum}
                  selected={selected(id)}
                />
              </NavigationMenuItem>
            );
          })}

          <NavigationMenuItem>
            <Divider vertical />
          </NavigationMenuItem>

          {filteredTeammatesMenu.map((mate) => {
            const { text, icon, onClick, sum, id } = mate;
            return (
              <NavigationMenuItem key={id}>
                <NavigationItem
                  tooltipContent={text}
                  icon={icon}
                  onClick={onClick}
                  selected={selected(id)}
                  sum={sum}
                />
              </NavigationMenuItem>
            );
          })}

          <NavigationMenuItem>
            <Popover open={agentsOpen} onOpenChange={setAgentsOpen}>
              <PopoverTrigger asChild>
                <div>
                  <InfoTooltip content={t('conversation.teammates')} side="bottom">
                    <span>
                      <MenuButton icon={<EllipsisIcon size={20} />} selected={agentsOpen} />
                    </span>
                  </InfoTooltip>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-1" align="center" side="bottom" sideOffset={4}>
                <PinMenuContent items={normalizeTeammatesForPinMenu} />
              </PopoverContent>
            </Popover>
          </NavigationMenuItem>

          {departmentsMenu.length > 0 && (
            <>
              <NavigationMenuItem>
                <Divider vertical />
              </NavigationMenuItem>

              {filteredDepartmentsMenu.map(({ text, icon, onClick, sum, id }) => {
                return (
                  <NavigationMenuItem key={id}>
                    <NavigationItem
                      tooltipContent={text}
                      icon={icon}
                      onClick={onClick}
                      selected={selected(id)}
                      sum={sum}
                    />
                  </NavigationMenuItem>
                );
              })}

              <NavigationMenuItem>
                <Popover open={departmentsOpen} onOpenChange={setDepartmentsOpen}>
                  <PopoverTrigger asChild>
                    <div>
                      <InfoTooltip content={t('conversation.departments')} side="bottom">
                        <span>
                          <MenuButton
                            icon={<EllipsisIcon size={20} />}
                            selected={departmentsOpen}
                          />
                        </span>
                      </InfoTooltip>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-1"
                    align="center"
                    side="bottom"
                    sideOffset={4}
                  >
                    <PinMenuContent
                      items={normalizeDepartmentsForPinMenu}
                      showScrollArea={true}
                      maxHeight="max-h-64"
                    />
                  </PopoverContent>
                </Popover>
              </NavigationMenuItem>
            </>
          )}
        </NavigationMenuList>
      </NavigationMenu>
      {!!serviceDesks?.length && (
        <DeskDropdown
          serviceDeskOnly
          showSingle
          variant="glossy"
          getPath={(id) => `/${slug}/chats/${id}/conversations`}
        />
      )}
    </nav>
  );
};

export default ChatNavigationIframe;
